import * as Page from '../../components/content/page-header';
import * as Button from '../../components/button/button';
import { Icons } from '../../components/icons/icons';
import { useNavigate, useLocation } from 'react-router';
import { Link } from 'react-router-dom';
import { PaginatedTable } from '../../components/table/paginated-table';
import { MainContent } from '../../components/layout/content';
import usePageState from '../../hooks/use-page-state';
import { Modes } from '../../types/page-modes';
import { Actions, Content, Dialog, Title } from '../../components/dialog/dialog';
import React, { useMemo, useState } from 'react';
import { Query, usePagination } from '../../hooks/use-pagination';
import queryString from 'query-string';
import {
  ActionColumn,
  CellRenderer,
  GearIconColumn,
  ManuallyAddedColumn
} from '../../components/table/columns';
import {
  useDeleteMutation, usePostMutation,
} from '../../hooks/use-mutations';
import { MapServer } from './types/map-server';
import { Pageable } from '../../services/base-service';
import { KeyValue } from '../../types/key-value';
import { NotInSync } from '../../components/not-in-sync/not-in-sync';
import { RevertToMaster2 } from '../../components/revert-to-master/revert-to-master-2';
import { MapService, MapServicePollTypes } from './types/map-service';
import { CordSystem } from '../settings/types/cordinate-system';
import { RecordStatus } from '../../types/record-status';
import tenantService from '../../services/tenant-service';
import { SearchField, useSearch } from '../../hooks/use-search';
import { alert } from '../../components/toast/toast';

export const MapServices = (): JSX.Element => {
  const [pageState, setPageState] = usePageState();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const { mapServerId } = queryString.parse(search);
  const [query, setQuery] = useState<Query>({});
  const { data, isLoading } = usePagination<Pageable<MapService>>(mapServerId ? `/map-services/map-server/${mapServerId as string}` : '/map-services', query);
  const deleteMutation = useDeleteMutation();
  const { isSearching, searchData, setSearchQuery } = useSearch<MapService>({ path: '/map-services/search' });
  const mapServers = usePagination<Pageable<MapServer>>('/map-servers', { size: 9999 });
  const mapServersData = mapServers.data?.content || [];
  const coordSystems = usePagination<Pageable<CordSystem>>('/cord-systems', { size: 9999 });
  const coordSystemsData = coordSystems.data?.content || [];
  const pollMutation = usePostMutation({ path: `/map-services` });

  const mapServerMap = useMemo(() => {
    const map: KeyValue = {};
    mapServersData?.forEach((item): void => {
      map[item.id] = item.name;
    });
    return map;
  }, [mapServersData]);

  const coordSystemsMap = useMemo(() => {
    const map: KeyValue = {};
    coordSystemsData?.forEach((item): void => {
      map[item.id] = item.coordSysId;
    });
    return map;
  }, [coordSystemsData]);

  const onDeleteConfirm = (): void => {
    deleteMutation.mutate({ path: `/map-services/${pageState.item?.id as string}` });
    setPageState(Modes.BROWSE);
  };

  const onPollClick = async (pollType: MapServicePollTypes): Promise<void> => {
    try {
      setPageState(Modes.BROWSE);
      await pollMutation.mutateAsync({ path: `/map-services/${pageState.item?.id as string}/poll?type=${pollType}` });
      alert.success('Poll queued successfully');
    } catch {
      alert.error('Failed to queue poll');
    }
  };

  const columns = [
    ...GearIconColumn(),
    { accessor: 'slug', Header: 'Map Service', sort: true, Cell: CellRenderer },
    { accessor: 'mapServerId', Header: 'Map Server Id', sort: true, Cell: (item: any): JSX.Element => {
      const row = item.row.original as MapService;
      const className = item.row.original.recordStatus === RecordStatus.GM_LOCKED && !tenantService.isGoldMaster() ? 'locked-column' : '';
      return <span className={className}>{mapServerMap[row.mapServerId as keyof MapService]}</span>;
    } },
    { accessor: 'cordSysId', Header: 'Coordinate System Id', sort: true, Cell: (item: any): JSX.Element => {
      const row = item.row.original as MapService;
      const className = item.row.original.recordStatus === RecordStatus.GM_LOCKED && !tenantService.isGoldMaster() ? 'locked-column' : '';
      return <span className={className}>{coordSystemsMap[row.cordSysId as keyof MapService]}</span>;
    } },
    { accessor: 'protocolId', Header: 'Protocol', Cell: CellRenderer },
    { accessor: 'countLayers', Header: 'Map Layers', Cell: (item: any): JSX.Element => {
      const row = item.row.original as MapService;
      return <Link to={`/map-layers?mapServiceId=${row?.id as string}`} className="text-decoration-none">{row.countLayers}</Link>;
    } },
    ManuallyAddedColumn(),
    { accessor: 'isPollingDisabled', Header: 'Poll', Cell: (item: any): JSX.Element => {
        const row = item.row.original as MapService;
        return <PollRenderer disabled={row.isPollingDisabled} onClick={(): void => setPageState(Modes.CREATE, row)} />;
    } },
    ActionColumn({
      onDelete: (item: MapServer): void => setPageState(Modes.DELETE, item),
      onUpdate: (item: MapServer): void => navigate(`/map-services/${item.id}`)
    })
  ];

  return <>
    <Page.Header>
      <Page.Tenant />
      <Page.Title>Map Services <NotInSync path="map-services" /></Page.Title>
      <Page.SubTitle>PDC hosted map services as well as external map servers.</Page.SubTitle>
      <Page.Actions>
        {!mapServerId ? <SearchField /> : <span />}
        <div>
          <RevertToMaster2 path="map-services" />
          <Button.PrimarySmall onClick={(): void => navigate(`${pathname}/new`)}>
            <Icons.Add />
            Add Map Service
          </Button.PrimarySmall>
        </div>
      </Page.Actions>
    </Page.Header>
    <MainContent>
      {isSearching
        ? <PaginatedTable isLoading={isLoading} columns={columns} data={searchData} onChange={setSearchQuery} />
        : <PaginatedTable isLoading={isLoading} columns={columns} data={data} onChange={setQuery} />
      }
    </MainContent>
    {pageState.mode === Modes.DELETE && (
      <Dialog onCancel={(): void => setPageState(Modes.BROWSE)} backdropClose={false}>
        <Title>Delete Map Server</Title>
        <Content>Are you sure you want to delete?</Content>
        <Actions>
          <Button.Tertiary onClick={(): void => setPageState(Modes.BROWSE)}>Cancel</Button.Tertiary>
          <Button.Danger onClick={onDeleteConfirm}>Delete</Button.Danger>
        </Actions>
      </Dialog>
    )}
    {pageState.mode === Modes.CREATE && (
      <Dialog onCancel={(): void => setPageState(Modes.BROWSE)} backdropClose={false}>
        <Title>Poll Map Service</Title>
         <Content>
           <div className="mb-3">
             <Button.LightBlackSmall className="d-flex align-items-center w-100" onClick={(): Promise<void> => onPollClick(MapServicePollTypes.MAP_SERVICE_LAYER_OVERWRITE)}>
               <Icons.Cloud /> Poll and overwrite all layers
             </Button.LightBlackSmall>
           </div>
           <div className="mb-3">
             <Button.LightBlackSmall className="d-flex align-items-center w-100" onClick={(): Promise<void> => onPollClick(MapServicePollTypes.MAP_SERVICE_NEW_LAYERS)}>
               <Icons.Cloud /> Poll for new layers
             </Button.LightBlackSmall>
           </div>
           <div>
             <Button.LightBlackSmall className="d-flex align-items-center w-100" onClick={(): Promise<void> => onPollClick(MapServicePollTypes.MAP_SERVICE_KEEP_LAYER_CHANGES)}>
               <Icons.Cloud /> Poll and keep layer customizations
             </Button.LightBlackSmall>
           </div>
         </Content>
        <Actions>
          <Button.Tertiary className="m-0" onClick={(): void => setPageState(Modes.BROWSE)}>Cancel</Button.Tertiary>
        </Actions>
      </Dialog>
    )}
  </>;
};

type PollRendererProps = {
  onClick: () => void;
  disabled?: boolean;
};

const PollRenderer = (props: PollRendererProps): JSX.Element => {
  if (props.disabled) {
    return <span>
      <Icons.PollDisabled />
    </span>;
  }

  return <span onClick={props.onClick} className="pointer">
    <Icons.Poll />
  </span>;
};
